import {Rose, Clock, MapPin} from 'lucide-react'
import React from 'react'
import {Link} from 'react-router-dom'
import {urlFor} from '../lib/sanity'

const placeholderImage = '/assets/images/placeholderEventImage.jpg'

function getDateParts(dateString) {
  const date = new Date(dateString)

  return {
    day: date.getDate(),
    month: new Intl.DateTimeFormat('nl-NL', {month: 'long'}).format(date),
    year: date.getFullYear(),
  }
}

function isSameDate(dateA, dateB) {
  if (!dateA || !dateB) return false

  const a = new Date(dateA)
  const b = new Date(dateB)

  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function formatDate(dateString) {
  if (!dateString) return ''

  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(dateString))
}

function formatDateRange(startDateTime, endDateTime) {
  if (!startDateTime) return ''

  if (!endDateTime || isSameDate(startDateTime, endDateTime)) {
    return formatDate(startDateTime)
  }

  const start = getDateParts(startDateTime)
  const end = getDateParts(endDateTime)

  if (start.year !== end.year) {
    return `${start.day} ${start.month} ${start.year} - ${end.day} ${end.month} ${end.year}`
  }

  if (start.month === end.month) {
    return `${start.day} - ${end.day} ${end.month} ${end.year}`
  }

  return `${start.day} ${start.month} - ${end.day} ${end.month} ${end.year}`
}

function EventCard({event, featured = false}) {
    const hasSanityImage = event.image?.asset?._ref || event.image?.asset?._id
  
    const imageSrc = hasSanityImage
      ? urlFor(event.image).width(900).url()
      : placeholderImage
  
    return (
      <Link
        to={`/events/${event.slug}`}
        className={`group block overflow-hidden rounded-2xl bg-white/60 hover:bg-white p-4 transition-all duration-300 hover:-translate-y-4 hover:shadow-[0_18px_40px_rgba(87,87,253,0.22)] ${
          featured ? 'min-h-[430px]' : ''
        }`}
      >
        <div className="overflow-hidden">
          <img
            src={imageSrc}
            alt={event.image?.alt || event.title || 'Evenement afbeelding'}
            className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
              featured ? 'aspect-[9/5]' : 'aspect-square'
            }`}
          />
        </div>
      <div className="pt-4 text-[#5757fd] font-sans">
        <h3 className={`leading-tight ${featured ? 'text-3xl' : 'text-2xl'}`}>
          {event.title}
        </h3>

        {event.organiser && (
          <div className="py-2 text-sm font-sans_bold flex items-center tracking-wide">
            <p>{event.organiser}</p>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-x-1.5 gap-y-2 text-xs font-sans_bold">
          {event.type && (
            <span className="items-center flex space-x-2 bg-background/60 w-fit p-2 px-4 rounded-full tracking-wide">
              <Rose className="w-4 h-4" />
              <p>{event.type}</p>
            </span>
          )}

          {event.startDateTime && (
            <span className="items-center flex space-x-2 bg-background/60 w-fit p-2 px-4 rounded-full tracking-wide">
              <Clock className="w-4 h-4" />
              <p>{formatDateRange(event.startDateTime, event.endDateTime)}</p>
            </span>
          )}

          {event.city && (
            <span className="items-center flex space-x-2 bg-background/60 w-fit p-2 px-4 rounded-full tracking-wide">
              <MapPin className="w-4 h-4" />
              <p>{event.city}</p>
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}

export default EventCard